import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import * as moment from 'moment';

/**
 * 4-Column Payment Reconciliation view — Task #20.
 *
 * Shows every str_escrow_payment_mapping row for the selected date + location,
 * one row per payment_confirm entry, with colour-coded leg status:
 *
 *   Percona (Wiki)  →  Auth.net batch  →  Bank MERCH DEP  →  QuickBooks JE
 *
 * Route: /escrow-report/payment-mapping
 * API:   GET /api/2.6/escrow/payment-mapping?runDate=YYYY-MM-DD&location=HI&page=0&size=200
 */
@Component({
	selector: 'app-escrow-payment-mapping',
	templateUrl: './escrow-payment-mapping.component.html',
	styleUrls: ['./escrow-payment-mapping.component.css'],
	standalone: false,
	providers: [MessageService]
})
export class EscrowPaymentMappingComponent implements OnInit {

	@ViewChild('mappingTable') mappingTable: Table;

	formLoading = false;

	// Date picker (default yesterday — that is when the nightly job last ran)
	runDate: Date;

	location: string = 'HI';
	locationOptions = [
		{ label: 'Hawaii (HI)', value: 'HI' },
		{ label: 'Utah (UT)',   value: 'UT' }
	];

	discrepancyFilter: string = 'ALL';
	discrepancyOptions = [
		{ label: 'All rows',          value: 'ALL' },
		{ label: 'Matched only',      value: 'MATCHED' },
		{ label: 'Discrepancies only',value: 'DISCREPANCY' },
		{ label: 'Auth.net missing',  value: 'AUTHNET_MISSING' },
		{ label: 'Bank missing',      value: 'BANK_MISSING' },
		{ label: 'QBO missing',       value: 'QBO_MISSING' },
		{ label: 'Amount mismatch',   value: 'AMOUNT_MISMATCH' }
	];

	// Summary counts
	totalRows       = 0;
	matchedRows     = 0;
	discrepancyRows = 0;

	// Optional: filter to a single reservation (cross-link from escrow report)
	filterReservationId: number | null = null;

	// Raw + filtered rows
	rows: any[]         = [];
	filteredRows: any[] = [];

	constructor(
		private http: HttpClient,
		private router: Router,
		private route: ActivatedRoute,
		private messageService: MessageService) {}

	ngOnInit(): void {
		// Check if we arrived via cross-link from escrow report with a specific reservation
		const resId = this.route.snapshot.queryParamMap.get('reservationId');
		this.filterReservationId = resId ? +resId : null;

		// Default to yesterday (the nightly job populates yesterday's data)
		const now = new Date();
		const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
		this.runDate = yesterday;
		this.loadMappings();
	}

	loadMappings(): void {
		this.formLoading = true;
		const runDateStr = moment(this.runDate).format('YYYY-MM-DD');
		// If a specific reservation is requested, pass it instead of date+location
		const resParam = this.filterReservationId
			? `&reservationId=${this.filterReservationId}`
			: '';
		const url = `${environment.apiUrl}/escrow/payment-mapping?runDate=${runDateStr}&location=${this.location}&page=0&size=500${resParam}`;
		this.http.get<ApiResponse>(url).subscribe({
			next: (res) => {
				try {
					const data = (res && res.data && (res.data as any).paymentMapping)
						? (res.data as any).paymentMapping : null;
					if (data) {
						this.totalRows       = data.totalRows       || 0;
						this.matchedRows     = data.matchedRows     || 0;
						this.discrepancyRows = data.discrepancyRows || 0;
						this.rows            = data.rows            || [];
						this.applyFilters();
						this.messageService.add({
							severity: 'success',
							summary: 'Loaded',
							detail: `${this.rows.length} payment rows for ${runDateStr} (${this.location})`,
							life: 2500
						});
					} else {
						this.clearData();
					}
				} catch (e) {
					console.error('escrow-payment-mapping parse error', e);
					this.clearData();
				}
				this.formLoading = false;
			},
			error: (err) => {
				console.error('escrow-payment-mapping load error', err);
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: err.message || 'Could not load payment mapping'
				});
				this.formLoading = false;
			}
		});
	}

	clearData(): void {
		this.totalRows = this.matchedRows = this.discrepancyRows = 0;
		this.rows = [];
		this.filteredRows = [];
	}

	applyFilters(): void {
		this.filteredRows = (this.rows || []).filter(r => {
			if (this.discrepancyFilter === 'ALL')         return true;
			if (this.discrepancyFilter === 'MATCHED')     return r.allLegsMatch === true;
			if (this.discrepancyFilter === 'DISCREPANCY') return r.allLegsMatch !== true;
			// specific discrepancy code
			return r.discrepancyType === this.discrepancyFilter;
		});
	}

	onRunDateChange(): void {
		if (this.runDate) {
			const d = this.runDate;
			this.runDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
		}
		this.loadMappings();
	}

	onLocationChange(): void {
		this.loadMappings();
	}

	/** Navigate to the escrow report and highlight a specific reservation. */
	gotoEscrowReport(reservationId: number): void {
		this.router.navigate(['/escrow-report'], { queryParams: { highlight: reservationId } });
	}

	/** Clear the reservation filter and show all rows for the selected date. */
	clearReservationFilter(): void {
		this.filterReservationId = null;
		this.router.navigate([], { queryParams: {}, replaceUrl: true });
		this.loadMappings();
	}

	gotoMain(): void {
		this.router.navigate(['/escrow-report']);
	}

	// ---- Formatting helpers ----

	formatCurrency(v: any): string {
		if (v == null) return '';
		const n = typeof v === 'number' ? v : Number(v);
		if (isNaN(n)) return '';
		return n.toLocaleString('en-US', {
			style: 'currency', currency: 'USD',
			minimumFractionDigits: 2, maximumFractionDigits: 2
		});
	}

	formatDate(d: any): string {
		if (!d) return '';
		return moment(d).format('MM/DD/YYYY');
	}

	formatDateTime(d: any): string {
		if (!d) return '';
		return moment(d).format('MM/DD/YYYY HH:mm');
	}

	// ---- Leg status helpers ----

	/** CSS class for the overall row status (matched vs discrepancy). */
	rowStatusClass(row: any): string {
		if (row.allLegsMatch) return 'status-matched';
		return 'status-discrepancy';
	}

	/** Pill label for discrepancy code. */
	discrepancyLabel(code: string): string {
		switch (code) {
			case 'AUTHNET_MISSING':  return 'Auth.net missing';
			case 'BANK_MISSING':     return 'Bank missing';
			case 'QBO_MISSING':      return 'QBO missing';
			case 'AMOUNT_MISMATCH':  return 'Amount mismatch';
			default:                 return code || '—';
		}
	}

	/** CSS class for a per-leg status indicator. */
	legClass(present: boolean): string {
		return present ? 'leg-ok' : 'leg-missing';
	}

	/** CSS class for discrepancy pill. */
	discrepancyClass(code: string): string {
		if (!code) return '';
		return 'disc-pill disc-' + code.toLowerCase().replace('_', '-');
	}

	/** Match rate percentage string. */
	get matchRate(): string {
		if (!this.totalRows) return '—';
		return ((this.matchedRows / this.totalRows) * 100).toFixed(1) + '%';
	}
}
